import React, { useEffect, useState } from 'react';
import {
  AlertFilters,
  AlertType,
  AlertPriority,
  ApplicationAlert,
} from '../../types/admin';
import type { PaginationData } from 'shared';
import { ALERT_CATALOG } from './alertCatalog';
import ApplicationAlertsTable from './ApplicationAlertsTable';
import Button from 'shared/components/UC/Button';
import Select from 'shared/components/UC/Select';
import styles from './AlertsDrawer.module.css';

export interface CreateAlertData {
  applicationNumber: string;
  alertType: AlertType;
  title: string;
  description: string;
  priority?: AlertPriority;
}

interface AlertsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  alerts: ApplicationAlert[];
  isLoading: boolean;
  pagination: PaginationData;
  onPageChange: (page: number) => void;
  filters: AlertFilters;
  onFilterChange: (key: keyof AlertFilters, value: string) => void;
  onTypesChange: (types: AlertType[]) => void;
  onClearFilters: () => void;
  searchTerm: string;
  onSearchChange: (v: string) => void;
  onResolveAlert: (alertId: string, resolutionNote: string) => void;
  onDismissAlert: (alertId: string, note: string) => void;
  onFolioClick: (folio: string) => void;
  totalOpen: number;
  totalResolved: number;
  totalDismissed: number;
  onCreateAlert: (data: CreateAlertData) => Promise<void>;
  typeCounts?: Partial<Record<AlertType, number>>;
}

const EMPTY_CREATE: CreateAlertData = {
  applicationNumber: '',
  alertType: AlertType.MANUAL,
  title: '',
  description: '',
  priority: AlertPriority.MEDIUM,
};

const AlertsDrawer: React.FC<AlertsDrawerProps> = ({
  isOpen,
  onClose,
  alerts,
  isLoading,
  pagination,
  onPageChange,
  filters,
  onFilterChange,
  onTypesChange,
  onClearFilters,
  searchTerm,
  onSearchChange,
  onResolveAlert,
  onDismissAlert,
  onFolioClick,
  totalOpen,
  totalResolved,
  totalDismissed,
  onCreateAlert,
  typeCounts,
}) => {
  const [showCreate, setShowCreate] = useState(false);
  const [createData, setCreateData] = useState<CreateAlertData>(EMPTY_CREATE);
  const [createError, setCreateError] = useState('');
  const [createLoading, setCreateLoading] = useState(false);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleFolioClick = (folio: string) => {
    onFolioClick(folio);
    onClose();
  };

  const handleCreateSubmit = async () => {
    setCreateError('');
    if (!createData.applicationNumber.trim()) {
      setCreateError('El folio de postulación es obligatorio.');
      return;
    }
    if (!createData.title.trim()) {
      setCreateError('El título es obligatorio.');
      return;
    }
    try {
      setCreateLoading(true);
      await onCreateAlert(createData);
      setShowCreate(false);
      setCreateData(EMPTY_CREATE);
    } catch (err) {
      setCreateError(
        err instanceof Error ? err.message : 'Error al crear la alerta.'
      );
    } finally {
      setCreateLoading(false);
    }
  };

  const catalogOptions = ALERT_CATALOG.map((e) => ({
    value: e.type,
    label: e.label,
  }));

  const priorityOptions = [
    { value: AlertPriority.LOW, label: 'Baja' },
    { value: AlertPriority.MEDIUM, label: 'Media' },
    { value: AlertPriority.HIGH, label: 'Alta' },
    { value: AlertPriority.URGENT, label: 'Urgente' },
  ];

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className={styles.backdrop} onClick={onClose} aria-hidden="true" />

      {/* Drawer panel */}
      <div
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label="Alertas de Postulaciones"
      >
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerText}>
            <h2 className={styles.title}>Alertas de Postulaciones</h2>
            <p className={styles.subtitle}>
              Alertas del sistema que requieren atención administrativa
            </p>
          </div>
          <div className={styles.headerActions}>
            <button
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Cerrar panel de alertas"
              type="button"
            >
              <i className="uc-icon">close</i>
            </button>
          </div>
        </div>

        {/* Stats + create action */}
        <div className={styles.stats}>
          <div className={styles.statsGroup}>
            <div className={`${styles.stat} ${styles.statOpen}`}>
              <span className={styles.statNum}>{totalOpen}</span>
              <span className={styles.statLabel}>Abiertas</span>
            </div>
            <div className={styles.statDivider} />
            <div className={`${styles.stat} ${styles.statResolved}`}>
              <span className={styles.statNum}>{totalResolved}</span>
              <span className={styles.statLabel}>Resueltas</span>
            </div>
            <div className={styles.statDivider} />
            <div className={`${styles.stat} ${styles.statDismissed}`}>
              <span className={styles.statNum}>{totalDismissed}</span>
              <span className={styles.statLabel}>Descartadas</span>
            </div>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setShowCreate(true);
              setCreateError('');
            }}
          >
            Crear alerta
          </Button>
        </div>

        {/* Body */}
        <div className={styles.body}>
          <ApplicationAlertsTable
            alerts={alerts}
            isLoading={isLoading}
            pagination={pagination}
            onPageChange={onPageChange}
            filters={filters}
            onFilterChange={onFilterChange}
            onTypesChange={onTypesChange}
            onClearFilters={onClearFilters}
            searchTerm={searchTerm}
            onSearchChange={onSearchChange}
            onResolveAlert={onResolveAlert}
            onDismissAlert={onDismissAlert}
            onFolioClick={handleFolioClick}
            showHeader={false}
            typeCounts={typeCounts}
          />
        </div>
      </div>

      {/* Create alert modal — rendered outside the drawer panel for z-index */}
      {showCreate && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowCreate(false)}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Crear alerta manual</h3>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Folio de postulación *
                </label>
                <input
                  type="text"
                  className={styles.formInput}
                  placeholder="Ej: AV26001"
                  value={createData.applicationNumber}
                  onChange={(e) =>
                    setCreateData((d) => ({
                      ...d,
                      applicationNumber: e.target.value,
                    }))
                  }
                />
              </div>
              <div className={styles.formGroup}>
                <Select
                  label="Tipo de alerta *"
                  id="create-alert-type"
                  value={createData.alertType}
                  onChange={(e) =>
                    setCreateData((d) => ({
                      ...d,
                      alertType: e.target.value as AlertType,
                    }))
                  }
                  options={catalogOptions}
                />
              </div>
              {createData.alertType === AlertType.MANUAL && (
                <div className={styles.formGroup}>
                  <Select
                    label="Prioridad"
                    id="create-alert-priority"
                    value={createData.priority ?? AlertPriority.MEDIUM}
                    onChange={(e) =>
                      setCreateData((d) => ({
                        ...d,
                        priority: e.target.value as AlertPriority,
                      }))
                    }
                    options={priorityOptions}
                  />
                </div>
              )}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Título *</label>
                <input
                  type="text"
                  className={styles.formInput}
                  placeholder="Título descriptivo de la alerta"
                  value={createData.title}
                  onChange={(e) =>
                    setCreateData((d) => ({ ...d, title: e.target.value }))
                  }
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Descripción</label>
                <textarea
                  className={styles.formTextarea}
                  rows={3}
                  placeholder="Detalle adicional sobre la alerta..."
                  value={createData.description}
                  onChange={(e) =>
                    setCreateData((d) => ({
                      ...d,
                      description: e.target.value,
                    }))
                  }
                />
              </div>
              {createError && <p className={styles.formError}>{createError}</p>}
            </div>
            <div className={styles.modalActions}>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowCreate(false);
                  setCreateData(EMPTY_CREATE);
                  setCreateError('');
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateSubmit}
                disabled={createLoading}
              >
                {createLoading ? 'Creando...' : 'Crear alerta'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AlertsDrawer;
